import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { map, noop } from 'lodash';
import MediaQuery from 'react-responsive';
import PropTypes from 'prop-types';
import React, { lazy, Suspense } from 'react';

import { ContentZonesService, LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import {
    NoSSR,
    AnalyticsService,
    EVENTS,
    MEDIA_QUERIES,
    ModalService,
    DATA_LAYER_VARIABLES as DATA_LAYER_VARS,
    AdobeService,
    ADOBE_TAGS,
} from '@sm360/widget-commons-general';
import { LOGIN } from 'login/constants/modalNames.js';

import TransactionSummaryUnit from 'login/components/Transaction/TransactionSummaryUnit/TransactionSummaryUnit.jsx';

import TransactionStateService from 'login/services/TransactionStateService/TransactionStateService.js';
import * as loginActions from 'login/actions/loginActions.js';

import 'login/components/Transaction/TransactionSummary/TransactionSummary.scss';
import TRANSACTION from 'login/constants/transaction.js';
import TAGS from 'login/constants/trackingTags';

// The webpackChunkName name here is intended to be public. Webpack will use it to name files in production and development modes.
const TransactionDetails = lazy(() =>
    import(/* webpackChunkName: "TransactionDetails" */ '../TransactionDetails/TransactionDetails.jsx')
);

class TransactionSummary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            showTransactionDetails: false,
            completedSteps: {},
            savedTime: 0,
        };
        this.handleClick = this.handleClick.bind(this);
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
        this.updateTransactionState = this.updateTransactionState.bind(this);
        this.onTransactionUpdateRequired = this.onTransactionUpdateRequired.bind(this);
    }

    componentDidMount() {
        if (this.props.isAuthenticated) {
            this.updateTransactionState(true);
        }

        const $window = WindowProxy.get();
        if ($window) {
            $window.document.addEventListener(EVENTS.GARAGE_ADDED, this.onTransactionUpdateRequired);
            $window.document.addEventListener(EVENTS.FINANCING_CREATED, this.onTransactionUpdateRequired);
            $window.document.addEventListener(EVENTS.TRADEIN_CREATED, this.onTransactionUpdateRequired);
        }
    }

    componentWillReceiveProps(nextProps) {
        const quoteCountChanged = nextProps.quoteCount !== this.props.quoteCount;

        if (nextProps.isAuthenticated || quoteCountChanged) {
            this.updateTransactionState(true);
        }
    }

    componentWillUnmount() {
        const $window = WindowProxy.get();
        if ($window) {
            $window.document.removeEventListener(EVENTS.GARAGE_ADDED, this.onTransactionUpdateRequired);
            $window.document.removeEventListener(EVENTS.FINANCING_CREATED, this.onTransactionUpdateRequired);
            $window.document.removeEventListener(EVENTS.TRADEIN_CREATED, this.onTransactionUpdateRequired);
        }
    }

    onTransactionUpdateRequired() {
        if (this.props.isAuthenticated) {
            this.updateTransactionState(true);
        }
    }

    /**
     * Update the component state with the transaction state
     * @param {boolean} forceUdpate Force to fetch completed steps if true
     */
    updateTransactionState(forceUdpate) {
        const stepStatusPromise = TransactionStateService.getStepStatus(forceUdpate, !!this.props?.isMultiDealerGarage);
        const savedTimePromise = TransactionStateService.getSavedTime(!!this.props?.isMultiDealerGarage);
        const quotesPromise = this.props.actions.loadQuotes(!!this.props?.isMultiDealerGarage);

        Promise.all([stepStatusPromise, savedTimePromise, quotesPromise]).then(([completedSteps, savedTime]) => {
            // Trade-in completed indication - dataLayer
            AnalyticsService.setDataLayerVariable(
                DATA_LAYER_VARS.ECOMMERCE_TRADEIN,
                completedSteps?.tradein ? 'Yes' : 'No'
            );

            // Finance application completed indication - dataLayer
            AnalyticsService.setDataLayerVariable(
                DATA_LAYER_VARS.ECOMMERCE_FINANCE,
                completedSteps?.finance ? 'Yes' : 'No'
            );
            this.setState({
                completedSteps,
                savedTime,
            });
        });
    }

    handleClick() {
        if (WindowProxy.get()) {
            if (!this.state.showTransactionDetails) {
                // attach/remove event handler
                WindowProxy.get().document.addEventListener('click', this.handleOutsideClick, false);
            } else {
                WindowProxy.get().document.removeEventListener('click', this.handleOutsideClick, false);
            }

            if (!this.state.showTransactionDetails && this.props.isAuthenticated) {
                TransactionStateService.getStepStatus(false, !!this.props?.isMultiDealerGarage).then(
                    (completedSteps) => {
                        this.setState((prevState) => {
                            return {
                                showTransactionDetails: !prevState.showTransactionDetails,
                                completedSteps,
                            };
                        });
                    }
                );
            } else {
                this.setState((prevState) => {
                    return { showTransactionDetails: !prevState.showTransactionDetails };
                });
            }
        }
    }

    handleOutsideClick(e) {
        if (this.node.contains(e.target) || ModalService.get(LOGIN).isOpen()) {
            return;
        }

        this.handleClick();
    }

    render() {
        const title = ContentZonesService.getContentZone(
            'widget.transaction',
            'widget.transaction.bar.title'
        ).getValue();

        const actions = map(TRANSACTION, (itemSlug, key) => {
            let trackItemClick = noop;
            const taggingFunction = TAGS[`MY_TRANSACTION__${itemSlug.label.toUpperCase()}`];
            if (taggingFunction && taggingFunction.call && !this.state.showTransactionDetails) {
                trackItemClick = () => {
                    if (this.props.hasAdobeTagging) {
                        AdobeService.eventTrigger(ADOBE_TAGS.MY_TRANSACTION_OVERLAY(key), 'global-overlay');
                    }
                    AnalyticsService.gaTrigger(taggingFunction(WindowProxy.location().pathname));
                };
            }
            return (
                <TransactionSummaryUnit
                    key={key}
                    itemSlug={itemSlug.label}
                    isCompleted={this.state.completedSteps[itemSlug.label]}
                    onClick={trackItemClick}
                />
            );
        });

        const closeButtonClass = ['TransactionSummary--closeButton'];
        const closeProgressTitleClass = ['TransactionSummary--wrapper--title sr-progressBar-bg-title'];

        if (this.state.showTransactionDetails) {
            closeButtonClass.push('showButton');
            closeProgressTitleClass.push('is-open');
        }

        return (
            <div
                className="TransactionSummary--wrapper sr-progressBar"
                ref={(node) => {
                    this.node = node;
                }}
            >
                <div
                    className={closeProgressTitleClass.join(' ')}
                    onClick={this.handleClick}
                    role="button"
                    tabIndex={0}
                    data-test="login.transaction.open"
                >
                    <span className="is-bold">{title}</span>
                </div>

                <ul className="TransactionSummaryUnit--wrapper" onClick={this.handleClick}>
                    {actions}
                </ul>

                <div className={closeButtonClass.join(' ')}>
                    <button className="sr-progressBar-closeButton" onClick={this.handleClick}>
                        <MediaQuery minWidth={MEDIA_QUERIES.TABLET.MIN}>
                            {LocaleService.t('TRANSACTION_PROGRESSION.CLOSE_BUTTON_LABEL')}
                            <icon className="icon-keyboard_arrow_up" />
                        </MediaQuery>
                        <MediaQuery maxWidth={MEDIA_QUERIES.MOBILE.MAX}>
                            <icon className="icon-x-config" />
                        </MediaQuery>
                    </button>
                </div>

                <NoSSR>
                    <Suspense fallback="">
                        <TransactionDetails
                            showTransactionDetails={this.state.showTransactionDetails}
                            onShowTransactionDetails={this.handleClick}
                            onCloseTransactionAction={this.onCloseTransactionAction}
                            isNewEnabled={this.props.isNewEnabled}
                            isUsedEnabled={this.props.isUsedEnabled}
                            usedUrl={this.props.usedUrl}
                            vehicleTradein={this.props.vehicleTradein}
                            isAuthenticated={this.props.isAuthenticated}
                            loginClick={this.props.loginClick}
                            savedTime={this.state.savedTime}
                            completedSteps={this.state.completedSteps}
                            deleteTradeIn={this.props.deleteTradeIn}
                            isMultiDealerGarage={!!this.props?.isMultiDealerGarage}
                            hasAdobeTagging={this.props.hasAdobeTagging}
                            displayCreditRate={this.props.displayCreditRate}
                        />
                    </Suspense>
                </NoSSR>
            </div>
        );
    }
}

TransactionSummary.defaultProps = {
    isMultiDealerGarage: false,
    hasAdobeTagging: false,
    displayCreditRate: false,
};

TransactionSummary.propTypes = {
    actions: PropTypes.shape({
        loadQuotes: PropTypes.func.isRequired,
    }).isRequired,
    isNewEnabled: PropTypes.bool.isRequired,
    isUsedEnabled: PropTypes.bool.isRequired,
    usedUrl: PropTypes.string.isRequired,
    vehicleTradein: PropTypes.shape({
        tradeId: PropTypes.string.isRequired,
        make: PropTypes.string.isRequired,
        model: PropTypes.string.isRequired,
        modelTrim: PropTypes.string.isRequired,
        year: PropTypes.number.isRequired,
        odometer: PropTypes.number.isRequired,
        retail: PropTypes.number.isRequired,
        amount: PropTypes.number.isRequired,
        officialAmount: PropTypes.number.isRequired,
        officialAmountExp: PropTypes.number.isRequired,
    }).isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    loginClick: PropTypes.func.isRequired,
    quoteCount: PropTypes.number.isRequired,
    deleteTradeIn: PropTypes.func.isRequired,
    isMultiDealerGarage: PropTypes.bool.isRequired,
    hasAdobeTagging: PropTypes.bool,
    displayCreditRate: PropTypes.bool,
};

TransactionSummary.displayName = 'TransactionSummary';

function mapStateToProps(state) {
    return {
        quoteCount: state.quotes ? state.quotes.length : 0,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(loginActions, dispatch),
    };
}

export { TransactionSummary };
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TransactionSummary);
